import { useState } from 'react'
import { Users, UsersRound, MessageSquare, Package, Sparkles, Image, NotebookText, Plus, X, User, Settings, LogOut } from 'lucide-react'
import { cn } from '@/lib/utils'

interface BottomNavBarProps {
  activeTab: 'members' | 'chat' | 'private' | 'items' | 'abilities' | 'media' | 'notes'
  totalUnread?: number
  groupUnread?: number
  onTabChange: (tab: 'members' | 'chat' | 'private' | 'items' | 'abilities' | 'media' | 'notes') => void
  onNavigate?: (path: string) => void
  onOpenSettings?: () => void
}

export function BottomNavBar({ activeTab, totalUnread = 0, groupUnread = 0, onTabChange, onNavigate, onOpenSettings }: BottomNavBarProps) {
  const [showMore, setShowMore] = useState(false)
  
  // Tabs principais (sempre visíveis)
  const mainTabs = [
    { id: 'chat' as const, icon: Users, label: 'Chat' },
    { id: 'private' as const, icon: MessageSquare, label: 'Privado' },
    { id: 'members' as const, icon: UsersRound, label: 'Membros' },
  ]
  
  // Tabs extras (no menu +)
  const extraTabs = [
    { id: 'items' as const, icon: Package, label: 'Itens' },
    { id: 'abilities' as const, icon: Sparkles, label: 'Habilidades' },
    { id: 'media' as const, icon: Image, label: 'Mídias' },
    { id: 'notes' as const, icon: NotebookText, label: 'Anotações' },
  ]

  const handleExtraTabClick = (tabId: 'members' | 'chat' | 'private' | 'items' | 'abilities' | 'media' | 'notes') => {
    onTabChange(tabId)
    setShowMore(false)
  }

  // Verificar se alguma tab extra está ativa
  const isExtraTabActive = extraTabs.some(tab => tab.id === activeTab)

  return (
    // Fase B mobile: a barra vive no fluxo da coluna flex (sem fixed).
    // O wrapper relative ancora o menu "Mais", que abre logo acima da barra.
    <div className="relative flex-shrink-0 z-30">
      {/* Overlay para fechar menu (sobreposicao legitima, continua fixed) */}
      {showMore && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-20"
          onClick={() => setShowMore(false)}
        />
      )}

      {/* Menu expandido com tabs extras — ancorado acima da barra */}
      {showMore && (
        <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-game-secondary dark:bg-zinc-900/95 backdrop-blur-xl border border-game-border-primary dark:border-zinc-800/80 rounded-2xl p-3 z-30 shadow-game-lg dark:shadow-2xl">
          {/* Linha 1: Tabs de conteúdo */}
          <div className="flex gap-2 pb-3 border-b border-game-border-primary dark:border-zinc-700/50">
            {extraTabs.map((tab) => {
              const isActive = activeTab === tab.id
              const Icon = tab.icon
              
              return (
                <button
                  key={tab.id}
                  onClick={() => handleExtraTabClick(tab.id)}
                  className={cn(
                    "flex flex-col items-center gap-1 px-4 py-3 rounded-xl transition-all duration-200",
                    isActive 
                      ? "text-violet-600 dark:text-violet-400 bg-violet-500/15" 
                      : "text-game-muted hover:text-game-primary dark:hover:text-zinc-200 hover:bg-game-hover dark:hover:bg-zinc-800/50"
                  )}
                >
                  <Icon className={cn(
                    "h-6 w-6 transition-all duration-200",
                    isActive && "drop-shadow-[0_0_8px_rgba(139,92,246,0.5)]"
                  )} />
                  <span className="text-xs font-medium">{tab.label}</span>
                </button>
              )
            })}
          </div>
          
          {/* Linha 2: Ações de navegação */}
          <div className="flex gap-2 pt-3">
            <button
              onClick={() => {
                onNavigate?.('/perfil')
                setShowMore(false)
              }}
              className="flex flex-col items-center gap-1 px-4 py-3 rounded-xl transition-all duration-200 text-game-muted hover:text-game-primary dark:hover:text-zinc-200 hover:bg-game-hover dark:hover:bg-zinc-800/50"
            >
              <User className="h-6 w-6" />
              <span className="text-xs font-medium">Perfil</span>
            </button>
            <button
              onClick={() => {
                onOpenSettings?.()
                setShowMore(false)
              }}
              className="flex flex-col items-center gap-1 px-4 py-3 rounded-xl transition-all duration-200 text-game-muted hover:text-game-primary dark:hover:text-zinc-200 hover:bg-game-hover dark:hover:bg-zinc-800/50"
            >
              <Settings className="h-6 w-6" />
              <span className="text-xs font-medium">Config</span>
            </button>
            <button
              onClick={() => {
                onNavigate?.('/pagina-inicial')
                setShowMore(false)
              }}
              className="flex flex-col items-center gap-1 px-4 py-3 rounded-xl transition-all duration-200 text-rose-400 hover:text-rose-300 hover:bg-rose-500/10"
            >
              <LogOut className="h-6 w-6" />
              <span className="text-xs font-medium">Sair</span>
            </button>
          </div>
        </div>
      )}

      {/* Barra de navegação principal (no fluxo; safe-area-pb cobre o home indicator) */}
      <div className="bg-game-secondary dark:bg-zinc-900/95 backdrop-blur-xl border-t border-game-border-primary dark:border-zinc-800/80 px-4 py-1 shadow-game-md dark:shadow-xl safe-area-pb">
        <div className="flex items-center justify-around max-w-sm mx-auto">
          {mainTabs.map((tab) => {
            const isActive = activeTab === tab.id
            const Icon = tab.icon
            
            return (
              <button
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                className={cn(
                  "relative flex flex-col items-center gap-0.5 px-4 py-1.5 rounded-xl transition-all duration-200 min-w-[60px]",
                  isActive
                    ? "text-violet-600 dark:text-violet-400 bg-violet-500/10"
                    : "text-game-muted hover:text-game-secondary dark:hover:text-zinc-300"
                )}
              >
                <Icon className={cn(
                  "h-5 w-5 transition-all duration-200",
                  isActive && "drop-shadow-[0_0_8px_rgba(139,92,246,0.5)]"
                )} />
                <span className="text-[10px] font-medium">{tab.label}</span>

                {/* Badge de mensagens privadas não lidas (como no rail do desktop) */}
                {tab.id === 'private' && totalUnread > 0 && (
                  <div className="absolute top-0 right-2.5 min-w-[18px] h-[18px] px-1 bg-emerald-500 rounded-full flex items-center justify-center shadow-lg animate-in zoom-in-50 duration-200">
                    <span className="text-[10px] font-bold text-white leading-none">
                      {totalUnread > 99 ? '99+' : totalUnread}
                    </span>
                  </div>
                )}

                {/* Badge de mensagens novas no chat em grupo */}
                {tab.id === 'chat' && groupUnread > 0 && (
                  <div className="absolute top-0 right-2.5 min-w-[18px] h-[18px] px-1 bg-emerald-500 rounded-full flex items-center justify-center shadow-lg animate-in zoom-in-50 duration-200">
                    <span className="text-[10px] font-bold text-white leading-none">
                      {groupUnread > 99 ? '99+' : groupUnread}
                    </span>
                  </div>
                )}
              </button>
            )
          })}
          
          {/* Botão Mais (+) */}
          <button
            onClick={() => setShowMore(!showMore)}
            className={cn(
              "flex flex-col items-center gap-0.5 px-4 py-1.5 rounded-xl transition-all duration-200 min-w-[60px]",
              (showMore || isExtraTabActive)
                ? "text-violet-600 dark:text-violet-400 bg-violet-500/10" 
                : "text-game-muted hover:text-game-secondary dark:hover:text-zinc-300"
            )}
          >
            {showMore ? (
              <X className="h-5 w-5 transition-all duration-200" />
            ) : (
              <Plus className={cn(
                "h-5 w-5 transition-all duration-200",
                isExtraTabActive && "drop-shadow-[0_0_8px_rgba(139,92,246,0.5)]"
              )} />
            )}
            <span className="text-[10px] font-medium">Mais</span>
          </button>
        </div>
      </div>
    </div>
  )
}